import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtEuro0, MONTHS_EL_SHORT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Save, Pencil, BarChart3, TrendingUp, Copy, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { BudgetVersions } from "@/components/BudgetVersions";

const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const r2 = (n) => Math.round((n || 0) * 100) / 100;

export default function BudgetVsActual({ endpoint, queryKey, title, subtitle, icon: TitleIcon = TrendingUp, isCost = false, versions = false }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [tab, setTab] = useState(canEdit ? "edit" : "compare");
  const [rows, setRows] = useState([]);
  const [growth, setGrowth] = useState(5);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, year],
    queryFn: async () => (await api.get(`${endpoint}?year=${year}`)).data,
  });
  useEffect(() => { if (data?.rows) setRows(data.rows.map((r) => ({ ...r, budget_months: { ...r.budget_months } }))); }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put(endpoint, { year, lines: rows.map((r) => ({ account_code: r.account_code, months: r.budget_months })) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey, year] }); toast.success("Ο προϋπολογισμός αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const setCell = (idx, m, val) => setRows((p) => p.map((r, i) => (i === idx ? { ...r, budget_months: { ...r.budget_months, [m]: Number(val) || 0 } } : r)));
  const sumM = (obj, months) => r2(months.reduce((s, m) => s + (Number(obj?.[m]) || 0), 0));
  const copyPrev = async () => {
    try {
      const prev = (await api.get(`${endpoint}?year=${year - 1}`)).data;
      const map = {};
      (prev.rows || []).forEach((r) => { map[r.account_code] = r.actual_months || {}; });
      const f = 1 + (Number(growth) || 0) / 100;
      setRows((rs) => rs.map((r) => ({ ...r, budget_months: Object.fromEntries(MONTH_KEYS.map((m) => [m, Math.round((Number(map[r.account_code]?.[m]) || 0) * f)])) })));
      setTab("edit");
      toast.success(`Αντιγράφηκαν τα πραγματικά ${year - 1} +${growth}% — έλεγξε & Αποθήκευσε`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadVersion = (vlines) => {
    const map = Object.fromEntries(vlines.map((l) => [l.account_code, l.months || {}]));
    setRows((rs) => rs.map((r) => ({ ...r, budget_months: Object.fromEntries(MONTH_KEYS.map((m) => [m, Number(map[r.account_code]?.[m]) || 0])) })));
    setTab("edit");
  };
  const exportXlsx = () => downloadXlsx(`${endpoint}/export.xlsx?year=${year}`, `${queryKey}_${year}.xlsx`);
  const cell = "w-[54px] rounded border border-[#E3DED4] px-1 py-1 text-xs text-right tabular outline-none focus:border-[#45423C]";

  const monthly = useMemo(() => MONTH_KEYS.map((m) => {
    const b = r2(rows.reduce((s, r) => s + (Number(r.budget_months?.[m]) || 0), 0));
    const a = r2(rows.reduce((s, r) => s + (Number(r.actual_months?.[m]) || 0), 0));
    return { m, b, a, v: isCost ? r2(b - a) : r2(a - b) };
  }), [rows, isCost]);
  const grand = useMemo(() => {
    const b = r2(monthly.reduce((s, x) => s + x.b, 0));
    const a = r2(monthly.reduce((s, x) => s + x.a, 0));
    return { b, a, v: isCost ? r2(b - a) : r2(a - b) };
  }, [monthly, isCost]);
  const chartData = useMemo(() => monthly.map((x) => ({ name: MONTHS_EL_SHORT[Number(x.m)], "Προϋπολογισμός": x.b, "Πραγματικά": x.a })), [monthly]);

  const tone = (b, a) => { if (Math.abs(a - b) < 0.5) return "text-[#6F6A60]"; const bad = isCost ? a > b : a < b; return bad ? "text-[#991B1B] font-semibold" : "text-[#6E7A63] font-semibold"; };
  const pctTone = (p) => (isCost ? (p <= 100 ? "text-[#6E7A63] font-semibold" : "text-[#991B1B] font-semibold") : (p >= 100 ? "text-[#6E7A63] font-semibold" : p >= 80 ? "text-[#6F6A60]" : "text-[#991B1B] font-semibold"));
  const pct = (b, a) => (b > 0 ? Math.round((a / b) * 100) : (a > 0 ? (isCost ? 999 : 100) : 0));

  const Tab = ({ id, icon: Icon, label }) => (
    <button data-testid={`bva-tab-${id}`} onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium border transition-colors ${tab === id ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="budget-vs-actual-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#45423C] flex items-center gap-2">
            <TitleIcon className="h-6 w-6 text-[#6E7A63]" /> {title}
          </h1>
          <p className="text-sm text-[#8A8577] mt-1">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Έτος</label>
          <select data-testid="bva-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C] bg-white">
            {[2025, 2026, 2027, 2028, 2029].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="bva-export" onClick={exportXlsx}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
            <Download className="h-4 w-4" /> Excel
          </button>
          {tab === "edit" && canEdit && (
            <button data-testid="bva-save" onClick={() => save.mutate()} disabled={save.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Save className="h-4 w-4" /> Αποθήκευση
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Tab id="compare" icon={BarChart3} label="Σύγκριση (ανά μήνα)" />
        {canEdit && <Tab id="edit" icon={Pencil} label="Προϋπολογισμός" />}
      </div>

      {versions && !isLoading && <BudgetVersions endpoint={endpoint} year={year} rows={rows} canEdit={canEdit} onLoad={loadVersion} />}

      {isLoading ? (
        <div className="text-sm text-[#8A8577]">Φόρτωση…</div>
      ) : tab === "edit" ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium text-[#45423C]" data-testid="bva-edit-heading">Προϋπολογισμός {year}</h2>
            <span className="text-xs text-[#6F6A60]">Διάλεξε έτος πάνω δεξιά (π.χ. {year + 1} για του χρόνου), όρισε ποσά ανά μήνα & λογαριασμό και πάτα Αποθήκευση.</span>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-3">
              <span className="text-sm text-[#6F6A60]">Γρήγορο στήσιμο — Αντιγραφή από {year - 1} (πραγματικά)</span>
              <span className="text-sm text-[#6F6A60]">+</span>
              <input data-testid="bva-growth" type="number" value={growth} onChange={(e) => setGrowth(e.target.value)}
                className="w-16 rounded border border-[#E3DED4] px-2 py-1 text-sm text-right outline-none focus:border-[#45423C]" />
              <span className="text-sm text-[#6F6A60]">%</span>
              <button data-testid="bva-copy-prev" onClick={copyPrev}
                className="inline-flex items-center gap-2 rounded-lg bg-[#45423C] text-white px-3 py-1.5 text-sm font-medium hover:bg-[#56524A]">
                <Copy className="h-4 w-4" /> Αντιγραφή
              </button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border border-[#E3DED4] bg-white">
            <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-[#45423C] text-white text-left uppercase tracking-wider text-[10px]">
                <th className="px-3 py-3 sticky left-0 bg-[#45423C] z-10">Λογαριασμός</th>
                {MONTH_KEYS.map((m) => <th key={m} className="px-1 py-3 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                <th className="px-2 py-3 text-right">Σύνολο</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const showGroup = r.group && (i === 0 || rows[i - 1].group !== r.group);
                return (
                  <React.Fragment key={r.account_code}>
                    {showGroup && (
                      <tr className="bg-[#F1EDE6]"><td colSpan={14} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#8A8577] sticky left-0 bg-[#F1EDE6]">{r.group}</td></tr>
                    )}
                    <tr data-testid={`bva-row-${r.account_code}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] bg-white">
                      <td className="px-3 py-1.5 font-medium text-[#45423C] sticky left-0 bg-white z-10">{r.name}</td>
                      {MONTH_KEYS.map((m) => (
                        <td key={m} className="px-1 py-1.5 text-right tabular">
                          <input data-testid={`bva-cell-${r.account_code}-${m}`} type="number" step="1" className={cell}
                            value={Math.round(Number(r.budget_months[m]) || 0)} onChange={(e) => setCell(i, m, e.target.value)} />
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-right tabular font-semibold text-[#45423C]">{fmtEuro0(sumM(r.budget_months, MONTH_KEYS))}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                <td className="px-3 py-3 sticky left-0 bg-[#F1EDE6] z-10">ΣΥΝΟΛΟ</td>
                {MONTH_KEYS.map((m) => <td key={m} className="px-1 py-3 text-right tabular">{fmtEuro0(rows.reduce((s, r) => s + (Number(r.budget_months[m]) || 0), 0))}</td>)}
                <td className="px-2 py-3 text-right tabular" data-testid="bva-budget-grand-total">{fmtEuro0(grand.b)}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[#E3DED4] bg-white p-4">
            <div className="text-sm font-semibold text-[#45423C] mb-3">Προϋπολογισμός vs Πραγματικά (ανά μήνα)</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6F6A60" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8A8577" }} width={54} />
                <Tooltip formatter={(v) => fmtEuro(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Προϋπολογισμός" fill="#B9AE9A" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Πραγματικά" fill="#6E7A63" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-[#E3DED4] bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E3DED4] text-sm font-semibold text-[#45423C]">Ανά μήνα</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left uppercase tracking-wider text-[11px] text-[#6F6A60]">
                    <th className="px-4 py-3">Μήνας</th>
                    <th className="px-3 py-3 text-right">Προϋπολογισμός</th>
                    <th className="px-3 py-3 text-right">Πραγματικά</th>
                    <th className="px-3 py-3 text-right">Απόκλιση</th>
                    <th className="px-3 py-3 text-right">% Επίτευξη</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((x) => {
                    const p = pct(x.b, x.a);
                    return (
                      <tr key={x.m} data-testid={`bva-month-${x.m}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                        <td className="px-4 py-2.5 font-medium text-[#45423C]">{MONTHS_EL_SHORT[Number(x.m)]}</td>
                        <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{fmtEuro(x.b)}</td>
                        <td className="px-3 py-2.5 text-right tabular">{fmtEuro(x.a)}</td>
                        <td className={`px-3 py-2.5 text-right tabular ${tone(x.b, x.a)}`}>{fmtEuro(x.v)}</td>
                        <td className={`px-3 py-2.5 text-right tabular ${pctTone(p)}`}>{p > 900 ? "—" : p + "%"}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                    <td className="px-4 py-3">ΣΥΝΟΛΟ ΕΤΟΥΣ</td>
                    <td className="px-3 py-3 text-right tabular" data-testid="bva-total-budget">{fmtEuro(grand.b)}</td>
                    <td className="px-3 py-3 text-right tabular" data-testid="bva-total-actual">{fmtEuro(grand.a)}</td>
                    <td className={`px-3 py-3 text-right tabular ${tone(grand.b, grand.a)}`} data-testid="bva-total-variance">{fmtEuro(grand.v)}</td>
                    <td className="px-3 py-3 text-right tabular">{grand.b > 0 ? Math.round((grand.a / grand.b) * 100) : 0}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-[#E3DED4] bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E3DED4] text-sm font-semibold text-[#45423C]">Ανά λογαριασμό (ετήσιο)</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left uppercase tracking-wider text-[11px] text-[#6F6A60]">
                    <th className="px-4 py-3">Λογαριασμός</th>
                    <th className="px-3 py-3 text-right">Προϋπολογισμός</th>
                    <th className="px-3 py-3 text-right">Πραγματικά</th>
                    <th className="px-3 py-3 text-right">Απόκλιση</th>
                    <th className="px-3 py-3 text-right">% Επίτευξη</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const b = sumM(r.budget_months, MONTH_KEYS);
                    const a = sumM(r.actual_months, MONTH_KEYS);
                    const v = isCost ? r2(b - a) : r2(a - b);
                    const p = pct(b, a);
                    return (
                      <tr key={r.account_code} data-testid={`bva-acc-${r.account_code}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                        <td className="px-4 py-2.5 font-medium text-[#45423C]">{r.name}</td>
                        <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{fmtEuro(b)}</td>
                        <td className="px-3 py-2.5 text-right tabular">{fmtEuro(a)}</td>
                        <td className={`px-3 py-2.5 text-right tabular ${tone(b, a)}`}>{fmtEuro(v)}</td>
                        <td className={`px-3 py-2.5 text-right tabular ${pctTone(p)}`}>{p > 900 ? "—" : p + "%"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-[#8A8577]">Η «Απόκλιση» εμφανίζεται θετική όταν είναι ευνοϊκή ({isCost ? "έξοδα κάτω από τον προϋπολογισμό" : "έσοδα πάνω από τον στόχο"}). Κόκκινο = δυσμενής απόκλιση.</p>
        </div>
      )}
    </div>
  );
}
